import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Loader2, ListChecks, Wrench, GitBranch, ShieldAlert, ClipboardList, ClipboardCheck, Settings2, Archive, GraduationCap, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

const MODULE_ICONS = { work_orders: Wrench, ecm: GitBranch, cap: ShieldAlert, procedures: ClipboardList, audits: ClipboardCheck, config_mgmt: Settings2, retention: Archive, qualifications: GraduationCap, maintenance: Settings2 };
const PRIORITY_COLORS = { critical: "#DC2626", urgent: "#DC2626", high: "#D97706", major: "#D97706", medium: "#F59E0B", normal: "#2563EB", low: "#94A3B8" };

export default function MyWorkPage({ onLogout, userRole, isFeatureEnabled }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try { const r = await axios.get(`${API}/my-work`, { headers: getAuthHeaders() }); setData(r.data); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#2563EB]" size={28} /></div></Layout>;

  const items = data?.items || [];
  const grouped = {};
  items.forEach(i => { const m = i.module; if (!grouped[m]) grouped[m] = []; grouped[m].push(i); });

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="my-work-page">
        <PageHeader title="My Work" subtitle={`${items.length} item(s) requiring your attention`} testId="my-work-title" />
        {items.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] p-12 text-center"><ListChecks size={36} className="mx-auto mb-2 text-[#CBD5E1]" /><p className="text-sm text-[#94A3B8]">All clear — no pending items.</p></div>
        ) : (
          <div className="space-y-4">
            {Object.entries(grouped).map(([module, moduleItems]) => {
              const Icon = MODULE_ICONS[module] || ListChecks;
              return (
                <div key={module} className="bg-white border border-[#E2E8F0]" data-testid={`my-work-${module}`}>
                  <div className="px-4 py-2.5 bg-[#FAFAFA] border-b border-[#E2E8F0] flex items-center gap-2">
                    <Icon size={14} className="text-[#64748B]" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">{module.replace(/_/g, " ")}</span>
                    <span className="text-[10px] text-[#94A3B8]">({moduleItems.length})</span>
                  </div>
                  <div className="divide-y divide-[#F1F5F9]">
                    {moduleItems.map(item => {
                      const pColor = PRIORITY_COLORS[item.priority] || "#64748B";
                      return (
                        <a key={item.id} href={item.link} className="px-4 py-3 flex items-center gap-3 hover:bg-[#FAFAFA] transition-colors block" data-testid={`task-${item.id}`}>
                          {item.priority && <div className="w-1.5 h-8 shrink-0" style={{ backgroundColor: pColor }} />}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-bold text-[#94A3B8] uppercase">{item.type}</span>
                              {item.status && <span className="text-[10px] px-1.5 py-0.5 bg-[#F8FAFC] border border-[#E2E8F0] text-[#64748B]">{item.status}</span>}
                            </div>
                            <p className="text-sm text-[#0F172A] mt-0.5 truncate">{item.title}</p>
                          </div>
                          {item.priority && <span className="text-[9px] font-bold uppercase shrink-0" style={{ color: pColor }}>{item.priority}</span>}
                        </a>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
